import React, { useCallback, useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  TicketFilterOptionsSelector,
  TicketsParamsSelector,
} from '../../../../store/tickets/selectors';
import { UserDataSelector } from 'store/user/selector';
import {
  changeCwTicketFilterParams,
  changeTicketFilterParams,
  changeCustomTicketStatusFilter,
} from '../../../../store/tickets';
import { customFilterNames, ticketFilters } from './constants';
import { ITicketsFilter } from './types';
import { ICustomFilterTicketStatus } from 'services/user/types';

import useStyles from '../../styles';
import { LOCAL_STORAGE_PSA } from '../../../../constants/storage';
import { PsaTypes } from '../../../../services/config/types';
import Autocomplete from 'components/Autocomplete/Autocomplete';
import { Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ITicketFilterOptions, ITicketsParams } from '../../../../services/tickets/types';
import { ThunkGetTickets, ThunkGetTicketsFiltersOptions } from '../../../../store/tickets/thunks';
import { tt } from '@i18n/tt';
import cx from 'classnames';

const TicketsFilters: React.FC<{ className?: string }> = ({ className }) => {
  const ticketFilterOptions = useAppSelector(TicketFilterOptionsSelector);
  const params = useAppSelector(TicketsParamsSelector);
  const { useCustomTicketStatusFilter, customFilterTicketStatuses } = useAppSelector(UserDataSelector);

  const dispatch = useAppDispatch();
  const classes = useStyles();

  const [statuses, setStatuses] = useState<ICustomFilterTicketStatus[]>([]);
  const [activeIndex, setActiveIndex] = useState(1);

  const isCustomFilter = !ticketFilterOptions.useDefaultsStatuses;

  const onFilterClick = useCallback(
    (
      filter: ITicketsFilter,
      filterOptions: ITicketFilterOptions,
      queryParams: ITicketsParams,
      isCustom: boolean
    ) => {
      const psa = localStorage.getItem(LOCAL_STORAGE_PSA);
      if (psa === PsaTypes[PsaTypes.Connectwise] && !isCustomFilter) {
        dispatch(changeCwTicketFilterParams(filter.isClosed));
        dispatch(ThunkGetTickets({ params: { ...queryParams, isClosed: filter.isClosed ?? null } }));
        return;
      }

      const filterValue = filter.filterValue ? (filterOptions[filter.filterValue] as string) : '';
      const payload: { statusId: string | null; ticketStatusId: number[] | string[] } = {
        statusId: filterValue,
        ticketStatusId: [],
      };

      if (isCustom) {
        payload.statusId = null;
        if (filterValue) {
          if (psa === PsaTypes[PsaTypes.SuperOps]) {
            payload.ticketStatusId = filterValue.split(',').map(e => e.toString());
          } else {
            payload.ticketStatusId = filterValue.split(',').map(e => +e);
          }
        }
      }

      dispatch(ThunkGetTickets({ params: { ...queryParams, ...payload } }));
      dispatch(changeTicketFilterParams(payload));
    },
    [dispatch, isCustomFilter]
  );

  useEffect(() => {
    if (isCustomFilter === undefined) {
      return;
    }
    dispatch(ThunkGetTicketsFiltersOptions())
      .unwrap()
      .then(res => {
        const initial = {
          ...ticketFilters[1],
          filterValue: !res.useDefaultsStatuses
            ? (customFilterNames[1] as keyof ITicketFilterOptions)
            : ticketFilters[1].filterValue,
        };
        onFilterClick(initial, res, {
          page: 0,
          pageSize: 10,
          search: '',
          statusId: '',
          ticketStatusId: [],
          isClosed: null,
          _sort: 'id',
          _order: 1,
        }, !res.useDefaultsStatuses);
      });
  }, [isCustomFilter]);

  const onSelectFilter = useCallback(
    (_: React.MouseEvent<HTMLElement>, idx: number | null) => {
      if (idx === null) return;
      setActiveIndex(idx);
      const base = ticketFilters[idx];
      const computed = {
        ...base,
        filterValue: isCustomFilter
          ? (customFilterNames[idx] as keyof ITicketFilterOptions)
          : base.filterValue,
      };
      onFilterClick(computed, ticketFilterOptions, params, isCustomFilter);
    },
    [isCustomFilter, onFilterClick, ticketFilterOptions, params]
  );

  const onSaveCustomFilterTicketStatuses = useCallback(() => {
    const statusIds = statuses.map(status => +status.value);
    dispatch(changeCustomTicketStatusFilter(statusIds));
  }, [statuses, dispatch]);

  const onStatusesChange = useCallback(
    (value: ICustomFilterTicketStatus[]) => {
      setStatuses(value);
      if (value.length === 0) {
        dispatch(changeCustomTicketStatusFilter([]));
      }
    },
    [dispatch]
  );

  return (
    <div className={cx(classes.ticketsFilters, className)}>
      <div className={classes.filtersGroup}>
        <div className={classes.filterGroupHeader}>{tt('pages.tickets.filters')}</div>

        <div className={classes.filtersList}>
          {!useCustomTicketStatusFilter ? (
            <ToggleButtonGroup
              exclusive
              value={activeIndex}
              onChange={onSelectFilter}
              className={classes.filtersList}
              fullWidth
            >
              {ticketFilters.map((filter, index) => (
                <ToggleButton
                  key={filter.name}
                  value={index}
                  className={classes.filter}
                >
                  <div className={classes.filterName}>
                    {filter.icon}
                    <div>{tt(filter.name as 'pages.tickets.tabs.all')}</div>
                  </div>
                  <div>{filter.quantity}</div>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          ) : (
            <div className={classes.customFilterTicketStatuses}>
              <Autocomplete
                options={customFilterTicketStatuses}
                onChange={onStatusesChange}
                placeholder={tt('pages.tickets.select')}
              />
              <div className={classes.saveButton}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onSaveCustomFilterTicketStatuses}
                >
                  {tt('pages.tickets.filterButton')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketsFilters;
